import type { SelectHTMLAttributes } from 'react';
import { ChangeEvent, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { createSelector } from 'reselect';

// Import styles
import './LocationPicker.module.scss'; // for globals/children overrides
import styles from './LocationPicker.module.scss';

// Selector: same as before
const selectLocationsList = createSelector([(state: any) => state.locations], (locations) => locations?.list ?? []);

// SUI-like onChange signature expected by callers: (e, { name, value })
type DropdownLikeChange = (e: ChangeEvent<HTMLSelectElement>, data: { name?: string; value: string }) => void;

interface LocationPickerProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  className?: string;
  clearable?: boolean;
  placeholder?: string;
  onChange?: DropdownLikeChange;
  value?: string;
  name?: string;
}

const LocationPicker = ({
  className,
  clearable = true,
  placeholder,
  onChange,
  value,
  name,
  id,
  disabled,
  ...rest
}: LocationPickerProps) => {
  const locations = useSelector(selectLocationsList);

  const options = useMemo(
    () =>
      locations?.map((location: any) => ({
        text: location.name,
        value: String(location.id),
      })),
    [locations]
  );

  // Strip SUI-only props so we don't pass unknown attrs to the DOM.
  const {
    search: _search,
    selection: _selection,
    selectOnBlur: _selectOnBlur,
    renderLabel: _renderLabel,
    ...domProps
  } = rest as any;

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const v = e.target.value;
    onChange?.(e, { name, value: v });
  };

  const mergedClassName = [styles.overflowFix, className].filter(Boolean).join(' ');
  const dataTestId = (rest as any)['data-testid'] ?? 'dropdown-item';

  return (
    <select
      id={id}
      name={name}
      className={mergedClassName}
      value={value ?? ''}
      onChange={handleChange}
      disabled={disabled}
      {...domProps}
      data-testid={dataTestId}
    >
      {/* Placeholder & clearable behavior */}
      {placeholder ? (
        <option value="" disabled={!clearable} hidden={!clearable && !!value}>
          {placeholder}
        </option>
      ) : (
        clearable && <option value="" />
      )}

      {options?.map((opt: { text: string; value: string }) => (
        <option key={opt.value} value={opt.value}>
          {opt.text}
        </option>
      ))}
    </select>
  );
};

export default LocationPicker;
